/**
 * Smart Prescription Intake™ — dental tab (Ricette).
 *
 * Flow:
 *  - List acquired prescriptions (cards)
 *  - "Scansiona Ricetta" → opens dialog with 3 modes:
 *     1. Camera scan (QR/barcode via html5-qrcode)
 *     2. Upload image/PDF (with optional auto-OCR Claude Vision)
 *     3. Keyboard scanner input (USB barcode reader, manual)
 *  - "Crea manuale" → minimal form for retro-archiving paper rx
 *  - Each card: preview, OCR fields, status pill, download, validate, delete
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  ScanLine, FileText, Upload, Plus, Loader2, Camera, Download,
  Trash2, CheckCircle2, AlertCircle, Sparkles, Keyboard, X,
  Search, FilePlus, Calendar, Eye,
} from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";

const STATUS_META = {
  acquired:     { label: "Acquisita",     cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",       icon: ScanLine },
  verified:     { label: "Verificata",    cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300", icon: CheckCircle2 },
  validated:    { label: "Verificata",    cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300", icon: CheckCircle2 }, // legacy alias
  ready_export: { label: "Pronta export", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",     icon: FileText },
  exported:     { label: "Esportata",     cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: CheckCircle2 },
  archived:     { label: "Archiviata",    cls: "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",     icon: FileText },
};

const SOURCE_LABEL = {
  camera:           "Camera",
  upload:           "Upload",
  scanner_keyboard: "Scanner USB",
  manual:           "Manuale",
};

const PrescriptionsTab = ({ clientId }) => {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [filter, setFilter] = useState("");
  const [openScan, setScan] = useState(false);
  const [openNew, setNew]   = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await axios.get(`${API}/dental/prescriptions/client/${clientId}`,
        { withCredentials: true });
      setItems(r.data.prescriptions || []);
    } catch {
      toast.error("Errore caricamento ricette");
    } finally { setLoad(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter(rx => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (rx.barcode_value || "").toLowerCase().includes(q)
        || (rx.notes || "").toLowerCase().includes(q)
        || JSON.stringify(rx.extracted || {}).toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="prescriptions-tab">
      {/* HERO */}
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5 dark:from-blue-500/10 dark:to-cyan-500/10 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow">
              <ScanLine className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600 dark:text-blue-400">Smart Prescription Intake™</p>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Ricette acquisite</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {items.length} {items.length === 1 ? "documento" : "documenti"} ·
                {" "}archiviazione automatica nella timeline clinica
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setNew(true)} className="rounded-full h-9" data-testid="rx-new-manual-btn">
              <FilePlus className="w-3.5 h-3.5 mr-1.5" /> Manuale
            </Button>
            <Button onClick={() => setScan(true)} className="rounded-full h-9 bg-gradient-to-r from-blue-500 to-cyan-500 text-white" data-testid="rx-scan-btn">
              <ScanLine className="w-3.5 h-3.5 mr-1.5" /> Scansiona Ricetta
            </Button>
          </div>
        </div>
      </div>

      {/* SEARCH */}
      {items.length > 0 && (
        <div className="relative">
          <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
          <Input
            placeholder="Cerca per codice, medico, prestazione, note..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="pl-9 rounded-2xl h-10"
            data-testid="rx-search"
          />
        </div>
      )}

      {/* LIST */}
      {loading ? (
        <div className="space-y-2">
          {[0,1].map(i => <div key={i} className="h-32 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState onScan={() => setScan(true)} hasFilter={!!filter.trim()} />
      ) : (
        <ul className="space-y-3" data-testid="rx-list">
          {visible.map(rx => (
            <RxCard key={rx.rx_id} rx={rx} onChanged={load} />
          ))}
        </ul>
      )}

      <ScanDialog open={openScan} onOpenChange={setScan} clientId={clientId} onCreated={load} />
      <ManualDialog open={openNew} onOpenChange={setNew} clientId={clientId} onCreated={load} />
    </div>
  );
};

/* ============================================================ */
const EmptyState = ({ onScan, hasFilter }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="rx-empty">
    <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 flex items-center justify-center mb-3">
      <ScanLine className="w-5 h-5 text-blue-500" />
    </div>
    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
      {hasFilter ? "Nessuna ricetta trovata" : "Nessuna ricetta ancora"}
    </p>
    <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
      {hasFilter ? "Prova a cambiare i criteri di ricerca." : "Acquisisci la prima ricetta SSN o privata in meno di 10 secondi."}
    </p>
    {!hasFilter && (
      <Button onClick={onScan} className="mt-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white" data-testid="rx-scan-empty">
        <ScanLine className="w-4 h-4 mr-1.5" /> Scansiona la prima
      </Button>
    )}
  </div>
);

/* ============================================================ */
const STATUS_ORDER = ["acquired", "verified", "ready_export", "exported", "archived"];

const RxCard = ({ rx, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const meta = STATUS_META[rx.status] || STATUS_META.acquired;
  const ex = rx.extracted || {};
  const fileUrl = `${API}/dental/prescriptions/${rx.rx_id}/file`;
  const isImage = (rx.file_mime || "").toLowerCase().startsWith("image/");
  const isPdf   = (rx.file_mime || "").toLowerCase() === "application/pdf";

  const setStatus = async (nextStatus) => {
    if (nextStatus === rx.status) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/dental/prescriptions/${rx.rx_id}`,
        { status: nextStatus }, { withCredentials: true });
      toast.success("Stato aggiornato");
      onChanged();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questa ricetta?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/dental/prescriptions/${rx.rx_id}`, { withCredentials: true });
      toast.success("Eliminata");
      onChanged();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const downloadFile = () => window.open(fileUrl, "_blank");

  // Whole-card click opens the preview when a file is available.
  // Defensive: even if `has_file` is missing (legacy records), allow click
  // when at least one file-related field exists.
  const hasFile = rx.has_file || rx.file_name || rx.file_mime;
  const openPreview = (e) => {
    // ignore clicks coming from interactive children (buttons, links)
    if (e.target.closest("button, a, [role='button']")) return;
    if (hasFile) setShowPreview(true);
  };

  return (
    <li
      className={`rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4 hover:border-blue-300 transition-all ${hasFile ? "cursor-pointer" : ""}`}
      onClick={openPreview}
      data-testid={`rx-card-${rx.rx_id}`}
    >
      <div className="flex items-start gap-3">
        {/* PREVIEW THUMBNAIL */}
        {hasFile && (
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="shrink-0 w-20 h-24 rounded-xl overflow-hidden border-2 border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-slate-800 hover:border-blue-400 transition-all relative group"
            title="Apri ricetta"
            data-testid={`rx-thumb-${rx.rx_id}`}
          >
            {isImage ? (
              <img src={fileUrl} alt="Ricetta" className="w-full h-full object-cover" loading="lazy" />
            ) : isPdf ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-rose-500 bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/20 dark:to-orange-900/20">
                <FileText className="w-7 h-7" />
                <span className="text-[8px] font-bold mt-1">PDF</span>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <FileText className="w-6 h-6" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Eye className="w-5 h-5 text-white" />
            </div>
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {rx.rx_number && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black font-mono px-2 py-0.5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900" data-testid={`rx-number-${rx.rx_id}`}>
                #{rx.rx_number}
              </span>
            )}
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>
              <meta.icon className="w-2.5 h-2.5" /> {meta.label}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              {SOURCE_LABEL[rx.source] || rx.source}
            </span>
            {ex.confidence && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                AI · {ex.confidence}
              </span>
            )}
          </div>
          {rx.barcode_value && (
            <p className="font-mono text-sm font-bold text-slate-900 dark:text-white mt-1.5 truncate">{rx.barcode_value}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px]">
            {ex.doctor_name && <Field label="Medico"     value={ex.doctor_name} />}
            {ex.prescription && <Field label="Prestazione" value={ex.prescription} />}
            {ex.issue_date && <Field label="Data"       value={ex.issue_date} />}
            {ex.exemption_code && <Field label="Esenzione" value={ex.exemption_code} />}
            {ex.doc_kind && <Field label="Tipo doc"    value={ex.doc_kind} />}
          </div>
          {rx.notes && <p className="text-[11px] italic text-slate-500 mt-2">📝 {rx.notes}</p>}
          <p className="text-[10px] text-slate-400 mt-2">
            <Calendar className="inline w-2.5 h-2.5 mr-0.5" />
            {fmtDate(rx.created_at)} · {rx.uploaded_by_name || "Sistema"}
          </p>
        </div>
      </div>

      {/* STATUS CHIP ROW — full workflow control */}
      <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-white/5">
        <p className="text-[9px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1.5">Cambia stato</p>
        <div className="flex flex-wrap gap-1" data-testid={`rx-status-chips-${rx.rx_id}`}>
          {STATUS_ORDER.map(s => {
            const m = STATUS_META[s];
            const active = (rx.status === s) || (rx.status === "validated" && s === "verified");
            return (
              <button
                key={s} type="button" disabled={busy || active}
                onClick={() => setStatus(s)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all
                  ${active
                    ? `${m.cls} ring-2 ring-offset-1 ring-current cursor-default`
                    : "bg-slate-100 dark:bg-white/5 text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10"}
                `}
                data-testid={`rx-status-set-${s}-${rx.rx_id}`}
              >
                <m.icon className="w-2.5 h-2.5" /> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        {hasFile && (
          <Button size="sm" variant="outline" onClick={downloadFile} className="h-7 rounded-full text-[11px]" data-testid={`rx-dl-${rx.rx_id}`}>
            <Download className="w-3 h-3 mr-1" /> Scarica
          </Button>
        )}
        {hasFile && (
          <Button size="sm" variant="outline" onClick={() => setShowPreview(true)} className="h-7 rounded-full text-[11px]" data-testid={`rx-view-${rx.rx_id}`}>
            <Eye className="w-3 h-3 mr-1" /> Vedi ricetta
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="h-7 w-7 p-0 ml-auto rounded-full text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20" data-testid={`rx-del-${rx.rx_id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Fullscreen preview */}
      {showPreview && hasFile && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
          data-testid={`rx-preview-modal-${rx.rx_id}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid={`rx-preview-close-${rx.rx_id}`}
          >✕</button>
          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-mono font-bold">
            #{rx.rx_number || rx.rx_id.slice(0,6)} · {meta.label}
          </div>
          <div className="relative max-w-5xl w-full max-h-[88vh] flex items-center justify-center" onClick={e => e.stopPropagation()}>
            {isImage ? (
              <img src={fileUrl} alt="Ricetta" className="max-w-full max-h-[88vh] object-contain rounded-xl shadow-2xl" />
            ) : isPdf ? (
              <iframe src={fileUrl} title="Ricetta PDF" className="w-full h-[88vh] rounded-xl bg-white shadow-2xl" />
            ) : (
              <div className="bg-white p-12 rounded-xl text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-sm text-slate-600">Formato file non supportato per anteprima.</p>
                <Button onClick={downloadFile} className="mt-4">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Scarica file
                </Button>
              </div>
            )}
            <a
              href={fileUrl} target="_blank" rel="noreferrer"
              className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 hover:bg-white text-slate-900 text-xs font-bold shadow-lg"
              onClick={e => e.stopPropagation()}
            >
              <Download className="w-3.5 h-3.5" /> Apri originale
            </a>
          </div>
        </div>
      )}
    </li>
  );
};

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{value}</p>
  </div>
);

/* ============================================================ */
/* SCAN DIALOG — camera + upload + USB scanner                  */
/* ============================================================ */
const ScanDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [mode, setMode]   = useState("camera");   // camera | upload | scanner
  const [barcode, setBarcode] = useState("");
  const [file, setFile]   = useState(null);     // {b64, mime, name}
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) { setMode("camera"); setBarcode(""); setFile(null); setPreview(null); setOcrPreview(null); }
  }, [open]);

  const handleFile = async (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File troppo grande (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const b64 = dataUrl.split(",")[1];
      setFile({ b64, mime: f.type, name: f.name });
      setPreview(f.type.startsWith("image/") ? dataUrl : null);
    };
    reader.readAsDataURL(f);
  };

  const runOcrPreview = async () => {
    if (!file || !file.mime.startsWith("image/")) {
      toast.error("Serve un'immagine per l'OCR");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dental/prescriptions/ocr`,
        { file_b64: file.b64, file_mime: file.mime }, { withCredentials: true });
      setOcrPreview(r.data.extracted || {});
      if (r.data.extracted?.barcode_value || r.data.extracted?.recipe_code) {
        setBarcode(r.data.extracted.barcode_value || r.data.extracted.recipe_code);
      }
      toast.success("Dati estratti");
    } catch (e) {
      toast.error("OCR fallito · puoi salvare comunque");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!barcode && !file) { toast.error("Acquisisci un codice o carica un file"); return; }
    setBusy(true);
    try {
      const payload = {
        barcode_value: barcode || null,
        source: mode === "camera" ? "camera" : mode === "upload" ? "upload" : "scanner_keyboard",
        file_b64:  file?.b64,
        file_name: file?.name,
        file_mime: file?.mime,
        extracted: ocrPreview || undefined,
        run_ocr:   !ocrPreview && !!file?.mime?.startsWith("image/"),
      };
      await axios.post(`${API}/dental/prescriptions/client/${clientId}`, payload,
        { withCredentials: true });
      toast.success("Ricetta acquisita ✓");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="rx-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <ScanLine className="w-3.5 h-3.5 text-white" />
            </div>
            Smart Prescription Intake™
          </DialogTitle>
          <DialogDescription>
            Acquisisci la ricetta tramite <b>fotocamera</b>, <b>upload file</b> o <b>scanner USB</b>.
            Per ciascun metodo puoi salvare anche l'immagine originale della ricetta.
          </DialogDescription>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="grid grid-cols-3 gap-1 p-1 rounded-2xl bg-slate-100 dark:bg-white/5">
          <ModeBtn active={mode==="camera"}  icon={Camera}   label="Fotocamera / Webcam" onClick={() => setMode("camera")} testid="rx-mode-camera" />
          <ModeBtn active={mode==="upload"}  icon={Upload}   label="Upload file"         onClick={() => setMode("upload")} testid="rx-mode-upload" />
          <ModeBtn active={mode==="scanner"} icon={Keyboard} label="Scanner USB"         onClick={() => setMode("scanner")} testid="rx-mode-scanner" />
        </div>

        {mode === "camera" && (
          <>
            <CameraScanPanel
              onResult={(code) => { setBarcode(code); toast.success(`Codice rilevato: ${code.slice(0,18)}…`); }}
              onSnapshot={(snap) => {
                setFile({ b64: snap.b64, mime: snap.mime, name: snap.name });
                setPreview(snap.dataUrl);
              }}
              barcode={barcode}
              hasSnapshot={!!file}
            />
            {preview && (
              <div className="rounded-2xl overflow-hidden bg-slate-100 dark:bg-white/5 max-h-40 flex items-center justify-center" data-testid="rx-camera-preview-img">
                <img src={preview} alt="ricetta" className="max-h-40 object-contain" />
              </div>
            )}
            {file?.mime?.startsWith("image/") && !ocrPreview && (
              <Button onClick={runOcrPreview} disabled={busy} variant="outline" size="sm" className="w-full rounded-2xl" data-testid="rx-run-ocr-camera">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                Estrai dati con AI Vision
              </Button>
            )}
          </>
        )}

        {mode === "upload" && (
          <div className="space-y-3">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-slate-300 dark:border-white/15 rounded-2xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
              data-testid="rx-upload-zone"
            >
              <Upload className="w-7 h-7 mx-auto text-slate-400 mb-2" />
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                {file ? file.name : "Trascina qui o clicca per caricare"}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">JPG · PNG · PDF · max 8MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="rx-upload-input"
              />
            </div>
            {preview && (
              <div className="rounded-2xl overflow-hidden bg-slate-100 dark:bg-white/5 max-h-48 flex items-center justify-center">
                <img src={preview} alt="preview" className="max-h-48 object-contain" />
              </div>
            )}
            {file?.mime?.startsWith("image/") && !ocrPreview && (
              <Button onClick={runOcrPreview} disabled={busy} variant="outline" className="w-full rounded-2xl" data-testid="rx-run-ocr">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                Estrai dati con AI Vision
              </Button>
            )}
          </div>
        )}

        {mode === "scanner" && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border border-violet-200 dark:border-violet-900/40 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Keyboard className="w-4 h-4 text-violet-600" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300">Scanner USB · Modalità tastiera</p>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                Punta lo scanner sul barcode della ricetta — il codice apparirà automaticamente qui sotto.
              </p>
              <Input
                autoFocus
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Aspettando lo scanner…"
                className="font-mono rounded-2xl"
                data-testid="rx-scanner-input"
              />
            </div>

            {/* Optional photo capture — for keeping the original image of the rx */}
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/15 p-3.5 bg-slate-50/40 dark:bg-white/[0.02]" data-testid="rx-scanner-photo-section">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-3.5 h-3.5 text-blue-500" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300">
                  Foto della ricetta <span className="text-slate-400 font-medium normal-case tracking-normal">(opzionale ma consigliata)</span>
                </p>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2 leading-snug">
                Aggiungi una foto della ricetta cartacea — sarà visibile cliccando sulla ricetta nella scheda paziente.
              </p>
              {!preview ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl text-xs"
                    data-testid="rx-scanner-upload-photo"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Carica foto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMode("camera")}
                    className="rounded-xl text-xs"
                    data-testid="rx-scanner-use-camera"
                  >
                    <Camera className="w-3.5 h-3.5 mr-1.5" /> Usa fotocamera
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="rounded-2xl overflow-hidden bg-slate-100 dark:bg-white/5 max-h-40 flex items-center justify-center" data-testid="rx-scanner-photo-preview">
                    <img src={preview} alt="ricetta" className="max-h-40 object-contain" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setFile(null); setPreview(null); setOcrPreview(null); }}
                    className="w-full rounded-xl text-xs"
                    data-testid="rx-scanner-remove-photo"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Rimuovi foto
                  </Button>
                  {file?.mime?.startsWith("image/") && !ocrPreview && (
                    <Button onClick={runOcrPreview} disabled={busy} variant="outline" size="sm" className="w-full rounded-xl text-xs" data-testid="rx-run-ocr-scanner">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                      Estrai dati con AI Vision
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Barcode input (visibile sempre) */}
        {mode !== "scanner" && (
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Codice barcode / NRE</label>
            <Input
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="Es. 0801231234567890123"
              className="font-mono rounded-2xl"
              data-testid="rx-barcode-input"
            />
          </div>
        )}

        {/* OCR preview */}
        {ocrPreview && (
          <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300">
                AI Vision · {ocrPreview.confidence || "low"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {ocrPreview.doctor_name && <Field label="Medico" value={ocrPreview.doctor_name} />}
              {ocrPreview.prescription && <Field label="Prestazione" value={ocrPreview.prescription} />}
              {ocrPreview.issue_date && <Field label="Data" value={ocrPreview.issue_date} />}
              {ocrPreview.exemption_code && <Field label="Esenzione" value={ocrPreview.exemption_code} />}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={busy || (!barcode && !file)}
            className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
            data-testid="rx-scan-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Acquisisci
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ModeBtn = ({ active, icon: Icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
      active ? "bg-white dark:bg-slate-900 shadow text-blue-600" : "text-slate-500 hover:bg-white/50"
    }`}
    data-testid={testid}
  >
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

/* ============================================================ */
/* CAMERA SCAN PANEL — html5-qrcode + frame snapshot             */
/* ============================================================ */
const CameraScanPanel = ({ onResult, onSnapshot, barcode, hasSnapshot }) => {
  const containerId = "rx-camera-scanner";
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(async () => {
    setError(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const inst = new Html5Qrcode(containerId);
      scannerRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 180 }, disableFlip: false },
        (decoded) => { onResult(decoded); },
        () => { /* per-frame errors silenced */ },
      );
      setScanning(true);
    } catch (e) {
      console.warn("camera start failed", e);
      setError("Camera non disponibile o permesso negato");
    }
  }, [onResult]);

  const stop = useCallback(async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch { /* ignore */ }
    setScanning(false);
  }, []);

  // Capture the current video frame as a JPEG snapshot and pass to parent.
  const snapshot = useCallback(() => {
    try {
      const videoEl = document.querySelector(`#${containerId} video`);
      if (!videoEl) {
        toast.error("Avvia prima la camera");
        return;
      }
      const w = videoEl.videoWidth || 640;
      const h = videoEl.videoHeight || 480;
      const cv = document.createElement("canvas");
      cv.width = w; cv.height = h;
      cv.getContext("2d").drawImage(videoEl, 0, 0, w, h);
      const dataUrl = cv.toDataURL("image/jpeg", 0.88);
      const b64 = dataUrl.split(",")[1];
      onSnapshot({
        b64,
        mime: "image/jpeg",
        name: `ricetta-${Date.now()}.jpg`,
        dataUrl,
      });
      toast.success("Foto catturata · l'immagine sarà salvata con la ricetta");
    } catch (e) {
      console.warn("snapshot failed", e);
      toast.error("Cattura immagine fallita");
    }
  }, [onSnapshot]);

  useEffect(() => () => { stop(); }, [stop]);

  return (
    <div className="rounded-2xl overflow-hidden bg-slate-900 relative" data-testid="rx-camera-panel">
      <div id={containerId} className="w-full min-h-[200px]" />
      {!scanning && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-900/95 p-6">
          {error ? (
            <>
              <AlertCircle className="w-6 h-6 text-amber-400" />
              <p className="text-xs text-slate-300 text-center max-w-xs">{error}</p>
              <Button onClick={start} variant="outline" size="sm" className="rounded-full border-slate-600 text-white" data-testid="rx-camera-retry">
                Riprova
              </Button>
            </>
          ) : (
            <>
              <Camera className="w-7 h-7 text-blue-300" />
              <p className="text-xs text-slate-300 text-center">
                Attiva la fotocamera per leggere il barcode<br/>
                <span className="text-slate-500 text-[10px]">e scattare la foto della ricetta</span>
              </p>
              <Button onClick={start} className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white" data-testid="rx-camera-start">
                <Camera className="w-4 h-4 mr-1.5" /> Avvia camera
              </Button>
            </>
          )}
        </div>
      )}
      {scanning && (
        <>
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Scansione attiva
          </div>
          {/* Snapshot button */}
          <div className="absolute bottom-2 right-2 flex flex-col gap-1.5">
            <Button
              onClick={snapshot}
              size="sm"
              className={`rounded-full text-white text-[11px] font-bold shadow-lg ${
                hasSnapshot
                  ? "bg-emerald-500 hover:bg-emerald-600"
                  : "bg-gradient-to-r from-blue-500 to-cyan-500"
              }`}
              data-testid="rx-camera-snapshot"
            >
              {hasSnapshot ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
              {hasSnapshot ? "Foto OK · Scatta di nuovo" : "Scatta foto"}
            </Button>
          </div>
        </>
      )}
      {barcode && (
        <div className="absolute bottom-2 left-2 right-32 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur px-3 py-1.5 text-xs font-mono font-bold text-slate-900 dark:text-white truncate">
          ✓ {barcode}
        </div>
      )}
    </div>
  );
};

/* ============================================================ */
/* MANUAL DIALOG                                                */
/* ============================================================ */
const ManualDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({});
  }, [open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.barcode_value && !form.prescription && !form.doctor_name) {
      toast.error("Compila almeno un campo");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/dental/prescriptions/client/${clientId}`, {
        barcode_value: form.barcode_value || null,
        source:        "manual",
        notes:         form.notes || null,
        extracted: {
          recipe_code:    form.barcode_value || "",
          doctor_name:    form.doctor_name   || "",
          prescription:   form.prescription  || "",
          exemption_code: form.exemption     || "",
          issue_date:     form.issue_date    || "",
          doc_kind:       form.doc_kind      || "",
          confidence:     "manual",
        },
      }, { withCredentials: true });
      toast.success("Ricetta archiviata");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rx-manual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
              <FilePlus className="w-3.5 h-3.5 text-white" />
            </div>
            Inserimento manuale
          </DialogTitle>
          <DialogDescription>Archivia retroattivamente una ricetta cartacea.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Field2 label="Codice / NRE" v={form.barcode_value} on={v => set("barcode_value", v)} tid="rx-m-code" mono />
          <div className="grid grid-cols-2 gap-2">
            <Field2 label="Data" v={form.issue_date} on={v => set("issue_date", v)} type="date" tid="rx-m-date" />
            <Field2 label="Esenzione" v={form.exemption} on={v => set("exemption", v)} tid="rx-m-exem" />
          </div>
          <Field2 label="Medico prescrittore" v={form.doctor_name} on={v => set("doctor_name", v)} tid="rx-m-doc" />
          <Field2 label="Prestazione" v={form.prescription} on={v => set("prescription", v)} tid="rx-m-presc" />
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Note</label>
            <Textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)} rows={2} className="rounded-2xl" data-testid="rx-m-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white" data-testid="rx-manual-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Archivia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field2 = ({ label, v, on, tid, type = "text", mono = false }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</label>
    <Input type={type} value={v || ""} onChange={e => on(e.target.value)} className={`rounded-2xl ${mono ? "font-mono" : ""}`} data-testid={tid} />
  </div>
);

export default PrescriptionsTab;
